/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError, CreateSubtenant, SubtenantResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type NewSubtenantMutationConfig = {
  onSuccess?: (data: SubtenantResponse, variables: CreateSubtenant) => void;
  onError?: (error: unknown, variables: CreateSubtenant) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: CreateSubtenant) => Promise<unknown> | void;
};

/**
 * Hook for creating new subtenant through the management plane API.
 *
 * @param config.name - String of the name of the new subtenant
 * @param config.onSuccess - Called after successful subtenant creation
 * @param config.onError - Called if subtenant creation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for creating new subtenant
 *
 * @example
 * ```tsx
 * const { mutate } = useNewSubtenantMutation({
 *   onSuccess: (data) => console.log('Subtenant Created:', data),
 *   onError: (error) => console.error('Subtenant Created failed:', error)
 *  },
 * });
 *
 * // Create Subtenant
 * mutate({ name: 'test-subtenant-0001', description: 'Subtenant description' });
 * ```
 */

export function useNewSubtenantMutation({ onSuccess, onError, onSettled, onMutate }: NewSubtenantMutationConfig) {
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error } = useMutation<SubtenantResponse, ApiError, Omit<CreateSubtenant, 'badgeColor'>>({
    mutationFn: (newSubtenant: CreateSubtenant) => {
      return mgmtPlaneAPIClient.subtenants.createSubtenant({ tenantId: tenantID!, requestBody: newSubtenant });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error };
}
